/**
 * A JELSZÓÁLLÍTÁS AZ IRODAI MEGHÍVÓ LINKJÉRŐL (#25).
 *
 * Az admin felvesz egy kollégát, a kolléga személyre szóló varázslinket kap,
 * a link ide mutat (`ADMIN_URL/#belepes=<token>`). Enélkül a képernyő nélkül a
 * meghívó sehova nem vitt: az új embernek nincs jelszava, a belépő űrlap
 * pedig jelszót kér (code review, 2026-09-11).
 *
 * A PWA `App.tsx`-ének mintájára — a két szerviz nem oszt kódot, a szerződés
 * a backendé (`/auth/magic-link/check`, `/redeem`). A HANG más: kolléga, nem
 * ügyfél — az `admin` névtér kulcsaival.
 *
 * A JELSZÓÁLLÍTÁS NEM NEGYEDIK FELÜLET. A `CLAUDE.md` szabálya a MUNKA-
 * képernyőkről szól; a belépés annak az előszobája, nem új tengely.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@/components/common';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError } from '@/lib/api-client';

/**
 * A backend `MagicLinkRedeemDto`-jának alsó határa. A felület ELŐRE mutatja,
 * hogy a kolléga ne egy 400-as válaszból tudja meg: kétszer begépelt egy túl
 * rövid jelszót.
 */
const PASSWORD_MIN = 10;

/**
 * A VARÁZSLINK TOKENJE A HORGONYBÓL JÖN (`…#belepes=<token>`), nem a
 * lekérdezésből — a backend `magicLinkUrl()`-je írja így. A horgonyt a
 * böngésző nem küldi el a szervernek, tehát a belépést érő titok nem kerül a
 * statikus kiszolgáló naplójába és a `Referer` fejlécbe.
 *
 * EZ A FÜGGVÉNY TISZTA — nem nyúl a címsorhoz. A React szigorú módja
 * fejlesztés közben KÉTSZER hívja az állapot-kezdőértéket; ha a törlés itt
 * lenne, a második hívás üres horgonyt találna. A törlés az `App`-ban, a
 * beolvasás UTÁN történik.
 */
export function readMagicLinkToken(): string | null {
  const match = /(?:^|&)belepes=([^&]+)/.exec(window.location.hash.replace(/^#/, ''));
  if (match === null) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // HIBÁS SZÁZALÉKJELRE (`#belepes=%`) a `decodeURIComponent` DOB, és ez
    // renderelés közben fut — egy kivétel fehér képernyőt adna az EGÉSZ
    // alkalmazás helyén. A levelezőprogramok át szokták írni a linkeket.
    return null;
  }
}

/** A szerver magyar mondata, ha jött — hálózati hibánál `null`, és a hívó ad sajátot. */
function serverMessage(caught: unknown): string | null {
  return caught instanceof ApiError && caught.message ? caught.message : null;
}

export interface SetPasswordProps {
  token: string;
  /** „Belépés jelszóval" — a token eldobása, vissza a belépő űrlapra. */
  onGiveUp: () => void;
}

export function SetPassword({ token, onGiveUp }: SetPasswordProps) {
  const { t } = useTranslation();
  const { checkMagicLink, redeemMagicLink } = useAuth();
  const [name, setName] = useState<string | null>(null);
  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A jelszó elmentődött, de a munkamenet nem állt fel — lásd `redeemMagicLink`. */
  const [passwordSet, setPasswordSet] = useState(false);

  // A LINK ÉRVÉNYESSÉGÉT ELŐRE MEGKÉRDEZZÜK. Enélkül a kolléga kétszer begépel
  // egy jelszót, és csak a küldés után derül ki, hogy a link már lejárt.
  useEffect(() => {
    let alive = true;

    checkMagicLink(token)
      .then((result) => {
        if (alive) {
          setValid(result.valid);
          setName(result.name);
        }
      })
      .catch(() => {
        // A hálózati hiba is „nem tudjuk megnyitni" — a teendő ugyanaz.
        if (alive) {
          setValid(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [checkMagicLink, token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    // A KÉT JELSZÓ EGYEZÉSE HELYI KÉRDÉS: a szervernek egy jelszót küldünk.
    if (password !== passwordAgain) {
      setError(t('auth.mismatch'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // `signedIn`-nél innen nincs teendő: az `App` a `me`-re vált.
      if ((await redeemMagicLink(token, password)) === 'passwordSet') {
        // ⚠️ AZ ŰRLAPON HAGYVA a kolléga újra beküldené, és a már elhasznált
        // tokenre „lejárt link" jönne — holott a jelszava rendben van.
        setPasswordSet(true);
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        // A link a két képernyő között járt le, vagy közben visszavonták
        // (újraküldés, inaktiválás).
        setValid(false);
        return;
      }
      setError(serverMessage(caught) ?? t('error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  if (valid === null) {
    // AZ ELLENŐRZÉS ALATT NINCS ŰRLAP. Egy felvillanó jelszómező, ami aztán
    // „nem érvényes"-re vált, rosszabb, mint a csend.
    return null;
  }

  if (passwordSet) {
    return (
      <main className="centered">
        <div className="card">
          <h1>{t('auth.linkTitle')}</h1>
          <output className="notice">{t('auth.passwordSaved')}</output>
          <button type="button" onClick={onGiveUp}>
            {t('auth.toSignIn')}
          </button>
        </div>
      </main>
    );
  }

  if (!valid) {
    return (
      <main className="centered">
        <div className="card">
          <h1>{t('auth.linkTitle')}</h1>
          <p className="error" role="alert">
            {t('auth.linkExpired')}
          </p>
          <button type="button" onClick={onGiveUp}>
            {t('auth.toSignIn')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="centered">
      <form className="card" onSubmit={onSubmit}>
        <h1>{t('auth.linkTitle')}</h1>
        {name === null ? null : <p className="subtitle">{t('auth.linkGreeting', { name })}</p>}
        <p className="subtitle">{t('auth.linkIntro')}</p>

        <label htmlFor="new-password">{t('auth.newPassword')}</label>
        <PasswordInput
          id="new-password"
          autoComplete="new-password"
          // A billentyűzet-első felület a fókusszal kezd, nem az egérrel.
          //
          // ⚠️ A `biome-ignore lint/a11y/noAutofocus` INNEN KIKERÜLT, és nem
          // azért, mert a szabály megengedte — hanem mert a `PasswordInput`
          // burkolás óta MEG SEM SZÓLAL. A Biome a11y-szabályai a natív
          // elemeket nézik; egy saját komponens mögé bújtatott `<input>`-en
          // némán elhallgatnak. Amit itt nyertünk (szemgolyó), azt egy
          // kapuból veszítettük — a következő `autoFocus` már nem kapna
          // figyelmeztetést sehol a projektben.
          autoFocus
          required
          minLength={PASSWORD_MIN}
          aria-describedby="new-password-hint"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <p id="new-password-hint" className="hint">
          {t('auth.passwordHint', { min: PASSWORD_MIN })}
        </p>

        <label htmlFor="new-password-again">{t('auth.passwordAgain')}</label>
        <PasswordInput
          id="new-password-again"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN}
          value={passwordAgain}
          onChange={(event) => setPasswordAgain(event.target.value)}
        />

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={busy}>
          {t('auth.setPassword')}
        </button>
      </form>
    </main>
  );
}
